import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import {
  ArrowLeft,
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  Edit,
  Trash2,
  AlertCircle,
  Check,
  X,
  ExternalLink,
  Calendar
} from 'lucide-react';

interface ClubDetail {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  address: string;
  mapsUrl: string;
  city: string;
  province: string;
  phone: string | null;
  email: string | null;
  hasParking: boolean;
  hasBathrooms: boolean;
  hasChangingRooms: boolean;
  hasBuffet: boolean;
  hasProShop: boolean;
  amenities: string[];
  isActive: boolean;
  createdAt: string;
  owner: {
    id: string;
    name: string;
    email: string;
    createdAt: string;
  };
  venues: {
    id: string;
    name: string;
    venueType: string;
    status: string;
    isActive: boolean;
    hourlyRate: number;
    _count: {
      bookings: number;
      reviews: number;
    };
  }[];
}

const AdminClubDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [club, setClub] = useState<ClubDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchClubDetail();
    }
  }, [id]);

  const fetchClubDetail = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/admin/clubs/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar club');
      }

      const data = await response.json();
      setClub(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/admin/clubs/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar club');
      }

      navigate('/admin/clubs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.textSecondary,
    fontSize: theme.fonts.sizeBody,
    cursor: 'pointer',
    marginBottom: '24px',
    fontFamily: theme.fonts.body,
    padding: '8px 0',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
  };

  const titleContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
  };

  const badgeStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    backgroundColor: isActive
      ? 'rgba(0, 230, 118, 0.1)'
      : 'rgba(255, 23, 68, 0.1)',
    color: isActive ? theme.colors.primary : theme.colors.danger,
  });

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: theme.fonts.body,
    border: 'none',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '1.2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '20px',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    color: theme.colors.text,
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '4px',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
  };

  const amenityBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '16px',
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    color: theme.colors.primary,
    fontSize: theme.fonts.sizeSmall,
    marginRight: '8px',
    marginBottom: '8px',
  };

  const venueCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.input,
    padding: '16px',
    border: `1px solid ${theme.colors.border}`,
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '32px',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
  };

  if (loading) {
    return (
      <div style={{
        ...containerStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ color: theme.colors.textSecondary }}>Cargando...</div>
      </div>
    );
  }

  if (error || !club) {
    return (
      <div style={containerStyle}>
        <button style={backButtonStyle} onClick={() => navigate('/admin/clubs')}>
          <ArrowLeft size={20} />
          Volver a Clubs
        </button>
        <div style={{
          ...cardStyle,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: theme.colors.danger
        }}>
          <AlertCircle size={24} />
          <span>{error || 'Club no encontrado'}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <button style={backButtonStyle} onClick={() => navigate('/admin/clubs')}>
        <ArrowLeft size={20} />
        Volver a Clubs
      </button>

      <div style={headerStyle}>
        <div style={titleContainerStyle}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            backgroundColor: 'rgba(0, 230, 118, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Building2 size={32} color={theme.colors.primary} />
          </div>
          <div>
            <h1 style={titleStyle}>{club.name}</h1>
            {club.slug && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '4px',
              }}>
                <span style={{
                  fontSize: theme.fonts.sizeSmall,
                  color: theme.colors.textSecondary
                }}>
                  /club/{club.slug}
                </span>
                <a
                  href={`/club/${club.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.colors.primary }}
                >
                  <ExternalLink size={14} />
                </a>
              </div>
            )}
          </div>
          <span style={badgeStyle(club.isActive)}>
            {club.isActive ? <Check size={14} /> : <X size={14} />}
            {club.isActive ? 'Activo' : 'Inactivo'}
          </span>
        </div>

        <div style={actionsStyle}>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: theme.colors.surface,
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
            }}
            onClick={() => navigate(`/admin/clubs/${id}/edit`)}
          >
            <Edit size={18} />
            Editar
          </button>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: 'rgba(255, 23, 68, 0.1)',
              color: theme.colors.danger,
            }}
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={18} />
            Desactivar
          </button>
        </div>
      </div>

      <div style={gridStyle}>
        {/* Club Info */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            <Building2 size={20} color={theme.colors.primary} />
            Informacion del Club
          </h3>

          {club.description && (
            <p style={{
              color: theme.colors.textSecondary,
              marginBottom: '20px',
              lineHeight: '1.6',
            }}>
              {club.description}
            </p>
          )}

          <div style={infoRowStyle}>
            <MapPin size={20} color={theme.colors.textSecondary} />
            <div>
              <div style={infoLabelStyle}>Direccion</div>
              <div>{club.address}</div>
              <div style={{ color: theme.colors.textSecondary }}>
                {club.city}, {club.province}
              </div>
            </div>
          </div>

          {club.phone && (
            <div style={infoRowStyle}>
              <Phone size={20} color={theme.colors.textSecondary} />
              <div>
                <div style={infoLabelStyle}>Telefono</div>
                <div>{club.phone}</div>
              </div>
            </div>
          )}

          {club.email && (
            <div style={infoRowStyle}>
              <Mail size={20} color={theme.colors.textSecondary} />
              <div>
                <div style={infoLabelStyle}>Email</div>
                <div>{club.email}</div>
              </div>
            </div>
          )}

          <div style={infoRowStyle}>
            <Calendar size={20} color={theme.colors.textSecondary} />
            <div>
              <div style={infoLabelStyle}>Creado</div>
              <div>{new Date(club.createdAt).toLocaleDateString('es-AR')}</div>
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <div style={infoLabelStyle}>Amenities</div>
            <div style={{ marginTop: '8px' }}>
              {club.hasParking && <span style={amenityBadgeStyle}><Check size={14} /> Estacionamiento</span>}
              {club.hasBathrooms && <span style={amenityBadgeStyle}><Check size={14} /> Banos</span>}
              {club.hasChangingRooms && <span style={amenityBadgeStyle}><Check size={14} /> Vestuarios</span>}
              {club.hasBuffet && <span style={amenityBadgeStyle}><Check size={14} /> Buffet</span>}
              {club.hasProShop && <span style={amenityBadgeStyle}><Check size={14} /> Pro Shop</span>}
            </div>
          </div>
        </div>

        {/* Owner Info */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            <User size={20} color={theme.colors.primary} />
            Propietario
          </h3>

          <div style={infoRowStyle}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 230, 118, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <User size={24} color={theme.colors.primary} />
            </div>
            <div>
              <div style={{ fontWeight: theme.fonts.weightBold, fontSize: '1.1rem' }}>
                {club.owner.name}
              </div>
              <div style={{ color: theme.colors.textSecondary }}>
                {club.owner.email}
              </div>
              <div style={{
                fontSize: theme.fonts.sizeSmall,
                color: theme.colors.textSecondary,
                marginTop: '4px',
              }}>
                Usuario desde {new Date(club.owner.createdAt).toLocaleDateString('es-AR')}
              </div>
            </div>
          </div>

          <button
            style={{
              ...buttonStyle,
              width: '100%',
              backgroundColor: theme.colors.surface,
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              marginTop: '16px',
            }}
            onClick={() => {
              // TODO: Implement change owner modal
              alert('Funcionalidad de cambiar propietario proximamente');
            }}
          >
            Cambiar Propietario
          </button>
        </div>
      </div>

      {/* Venues */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          <Building2 size={20} color={theme.colors.primary} />
          Canchas ({club.venues.length})
        </h3>

        {club.venues.length === 0 ? (
          <p style={{ color: theme.colors.textSecondary, textAlign: 'center', padding: '24px' }}>
            Este club aun no tiene canchas registradas
          </p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px',
          }}>
            {club.venues.map((venue) => (
              <div key={venue.id} style={venueCardStyle}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                }}>
                  <div>
                    <div style={{ fontWeight: theme.fonts.weightBold, marginBottom: '4px' }}>
                      {venue.name}
                    </div>
                    <div style={{ fontSize: theme.fonts.sizeSmall, color: theme.colors.textSecondary }}>
                      {venue.venueType}
                    </div>
                  </div>
                  <span style={badgeStyle(venue.isActive && venue.status === 'ACTIVE')}>
                    {venue.isActive && venue.status === 'ACTIVE' ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: theme.fonts.sizeSmall,
                  color: theme.colors.textSecondary,
                }}>
                  <span>${venue.hourlyRate}/hora</span>
                  <span>{venue._count.bookings} reservas</span>
                  <span>{venue._count.reviews} reviews</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={modalOverlayStyle} onClick={() => setShowDeleteModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 23, 68, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px auto',
            }}>
              <Trash2 size={30} color={theme.colors.danger} />
            </div>
            <h3 style={{
              ...titleStyle,
              fontSize: '1.3rem',
              marginBottom: '12px',
            }}>
              Desactivar Club?
            </h3>
            <p style={{
              color: theme.colors.textSecondary,
              marginBottom: '24px',
            }}>
              El club y todas sus canchas seran desactivados. Esta accion se puede revertir.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                }}
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: theme.colors.danger,
                  color: '#fff',
                }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Desactivando...' : 'Desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClubDetailPage;
